import flatpickr from "flatpickr";
import { French } from "flatpickr/dist/l10n/fr.js";
import CalendarAvailability from "./calendar-availability.js";

export default {
  async init() {
    // Vérifier que les éléments existent
    const dateFromElement = document.getElementById("date_from");
    const dateToElement = document.getElementById("date_to");

    if (!dateFromElement || !dateToElement) {
      console.warn("Datepicker elements not found, skipping initialization");
      return;
    }

    // Forcer les attributs sur mobile pour éviter le sélecteur natif
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    if (isMobile) {
      // S'assurer que les inputs sont bien de type text
      dateFromElement.type = "text";
      dateToElement.type = "text";

      // Ajouter des attributs pour désactiver le comportement natif
      [dateFromElement, dateToElement].forEach((element) => {
        element.setAttribute("inputmode", "none");
        element.setAttribute("autocomplete", "off");
        element.setAttribute("autocorrect", "off");
        element.setAttribute("autocapitalize", "off");
        element.setAttribute("spellcheck", "false");
        element.readOnly = true; // Rendre readonly pour forcer l'utilisation de flatpickr
      });
    }

    try {
      // Initialiser le service de disponibilité
      this.calendarAvailability = new CalendarAvailability();
      const unavailableDates =
        await this.calendarAvailability.fetchUnavailableDates();

      // Stocker la référence pour l'utiliser dans les callbacks
      const calendarAvailability = this.calendarAvailability;

      // Configuration pour le datepicker d'arrivée (logique inchangée)
      const arrivalConfig = {
        dateFormat: "d/m/Y",
        locale: French,
        minDate: "today",
        disable: unavailableDates, // Toutes les dates réservées sont interdites pour l'arrivée
        disableMobile: true, // IMPORTANT: Empêcher la création d'un input mobile séparé
        allowInput: false, // Empêcher la saisie manuelle
        clickOpens: true,
        onDayCreate: (dObj, dStr, fp, dayElem) => {
          const isUnavailable = calendarAvailability.isDateUnavailable(
            dayElem.dateObj,
          );

          if (isUnavailable) {
            dayElem.classList.add("unavailable-date");
            dayElem.title = "Réservé";
            dayElem.setAttribute("aria-disabled", "true");
            dayElem.style.pointerEvents = "none";
          }
        },
      };

      // Initialize start date picker
      const startDatePicker = flatpickr("#date_from", arrivalConfig);

      // Initialize end date picker avec logique spéciale pour les départs
      const endDatePicker = flatpickr("#date_to", {
        dateFormat: "d/m/Y",
        locale: French,
        minDate: "today",
        disable: [], // Pas de dates désactivées par défaut pour le départ
        disableMobile: true, // IMPORTANT: Empêcher la création d'un input mobile séparé
        allowInput: false, // Empêcher la saisie manuelle
        clickOpens: true,
        onDayCreate: (dObj, dStr, fp, dayElem) => {
          // Pour le départ, toutes les dates sont disponibles par défaut
          // (la logique de restriction se fait dans generateDisabledDatesForDeparture)
        },
      });

      // Solution supplémentaire pour mobile : forcer l'ouverture au touch/click
      if (isMobile) {
        // Vérifier qu'aucun input mobile n'a été créé
        if (startDatePicker.mobileInput || endDatePicker.mobileInput) {
          // Solution de secours : appliquer les mêmes fixes aux inputs mobiles
          const fixMobileInput = (picker, originalElement) => {
            if (picker.mobileInput) {
              picker.mobileInput.type = "text";
              picker.mobileInput.setAttribute("inputmode", "none");
              picker.mobileInput.setAttribute("autocomplete", "off");
              picker.mobileInput.setAttribute("autocorrect", "off");
              picker.mobileInput.setAttribute("autocapitalize", "off");
              picker.mobileInput.setAttribute("spellcheck", "false");
              picker.mobileInput.readOnly = true;

              // Cacher l'input mobile et forcer l'utilisation de l'input original
              picker.mobileInput.style.display = "none";
              originalElement.style.display = "block";
              originalElement.type = "text";

              // Approche plus agressive : supprimer complètement l'input mobile
              setTimeout(() => {
                if (picker.mobileInput && picker.mobileInput.parentNode) {
                  picker.mobileInput.remove();
                  picker.mobileInput = null;
                }
              }, 0);
            }
          };

          fixMobileInput(startDatePicker, dateFromElement);
          fixMobileInput(endDatePicker, dateToElement);
        }

        // Pour le champ date_to spécifiquement
        const addMobileTouchHandler = (element, picker) => {
          // Supprimer les anciens handlers
          element.removeEventListener("touchstart", element._touchHandler);
          element.removeEventListener("click", element._clickHandler);

          // Créer les nouveaux handlers
          element._touchHandler = (e) => {
            e.preventDefault();
            e.stopPropagation();
            if (!picker.isOpen) {
              picker.open();
            }
          };

          element._clickHandler = (e) => {
            e.preventDefault();
            e.stopPropagation();
            if (!picker.isOpen) {
              picker.open();
            }
          };

          // Ajouter les handlers
          element.addEventListener("touchstart", element._touchHandler, {
            passive: false,
          });
          element.addEventListener("click", element._clickHandler);
        };

        // Appliquer aux deux datepickers
        addMobileTouchHandler(dateFromElement, startDatePicker);
        addMobileTouchHandler(dateToElement, endDatePicker);

        // Vérifier périodiquement que les attributs sont toujours corrects
        const checkMobileAttributes = () => {
          [dateFromElement, dateToElement].forEach((element) => {
            if (element.type !== "text") {
              element.type = "text";
            }
            if (!element.readOnly) {
              element.readOnly = true;
            }
            // Vérifier aussi les attributs mobiles
            if (element.getAttribute("inputmode") !== "none") {
              element.setAttribute("inputmode", "none");
            }
          });
        };

        // Vérifier immédiatement et après un court délai
        checkMobileAttributes();
        setTimeout(checkMobileAttributes, 100);
        setTimeout(checkMobileAttributes, 500);

        // Surveiller les changements de visibilité du date_to
        const observer = new MutationObserver((mutations) => {
          mutations.forEach((mutation) => {
            if (
              mutation.type === "attributes" &&
              mutation.attributeName === "class"
            ) {
              checkMobileAttributes();
            }
          });
        });

        observer.observe(dateToElement.parentElement.parentElement, {
          attributes: true,
        });
      }

      // Fonction pour générer les dates bloquées pour le départ
      // NOUVELLE LOGIQUE SIMPLIFIÉE : Les dates réservées sont totalement indisponibles
      const generateDisabledDatesForDeparture = (arrivalDate) => {
        if (!arrivalDate) {
          // Si pas de date d'arrivée, permettre tous les départs
          return [];
        }

        const disabledDates = [];

        // 1. Bloquer les dates avant et y compris la date d'arrivée
        const today = new Date();
        const currentDate = new Date(today);

        while (currentDate <= arrivalDate) {
          disabledDates.push(new Date(currentDate));
          currentDate.setDate(currentDate.getDate() + 1);
        }

        // 2. NOUVELLE LOGIQUE : Bloquer directement toutes les dates indisponibles
        // (qui représentent maintenant les nuitées réservées)
        const firstReservedAfter =
          calendarAvailability.findFirstUnavailableDateAfter(arrivalDate);

        if (firstReservedAfter) {
          // Bloquer à partir de la première date réservée (plus de logique complexe)
          const oneYearLater = new Date(firstReservedAfter);
          oneYearLater.setFullYear(oneYearLater.getFullYear() + 1);

          const blockDate = new Date(firstReservedAfter);
          while (blockDate <= oneYearLater) {
            disabledDates.push(new Date(blockDate));
            blockDate.setDate(blockDate.getDate() + 1);
          }
        }

        return disabledDates;
      };

      // Ajouter le onChange au startDatePicker
      startDatePicker.config.onChange.push(
        (selectedDates, dateStr, instance) => {
          if (selectedDates.length > 0) {
            const arrivalDate = selectedDates[0];
            const nextDay = new Date(arrivalDate);
            nextDay.setDate(nextDay.getDate() + 1);

            // Générer les nouvelles restrictions
            const disabledDatesForDeparture =
              generateDisabledDatesForDeparture(arrivalDate);

            // Mettre à jour le datepicker de départ
            endDatePicker.set("minDate", nextDay);
            endDatePicker.set("disable", disabledDatesForDeparture);

            // NOUVELLE LOGIQUE SIMPLIFIÉE : Les dates réservées sont totalement indisponibles
            endDatePicker.config.onDayCreate = [
              (dObj, dStr, fp, dayElem) => {
                const arrivalDate = startDatePicker.selectedDates[0];
                let shouldDisable = false;
                let reason = "";

                if (arrivalDate) {
                  // Si c'est avant ou égal à la date d'arrivée
                  if (dayElem.dateObj <= arrivalDate) {
                    shouldDisable = true;
                    reason = "Date antérieure ou égale à l'arrivée";
                  }
                  // NOUVELLE LOGIQUE : Toute date réservée est indisponible (plus de logique complexe)
                  else if (calendarAvailability.isDateUnavailable(dayElem.dateObj)) {
                    shouldDisable = true;
                    reason = "Date réservée";
                  }
                }

                if (shouldDisable) {
                  dayElem.classList.add("unavailable-date");
                  dayElem.title = reason.includes("réservée")
                    ? "Réservée"
                    : "Indisponible";
                  dayElem.setAttribute("aria-disabled", "true");
                  dayElem.style.pointerEvents = "none";
                }
              },
            ];

            endDatePicker.redraw();

            // Vérifier si la date de départ actuelle est encore valide
            const currentEndDate = endDatePicker.selectedDates[0];
            if (
              currentEndDate &&
              (currentEndDate <= arrivalDate ||
                calendarAvailability.isDateUnavailable(currentEndDate))
            ) {
              endDatePicker.clear();
            }
          } else {
            // Remettre les restrictions par défaut (aucune restriction pour le départ)
            endDatePicker.set("minDate", "today");
            endDatePicker.set("disable", []);

            // Remettre l'onDayCreate par défaut (pas de restrictions)
            endDatePicker.config.onDayCreate = [
              (dObj, dStr, fp, dayElem) => {
                // Aucune restriction par défaut pour les dates de départ
              },
            ];

            endDatePicker.redraw();
          }
        },
      );

      this.startDatePicker = startDatePicker;
      this.endDatePicker = endDatePicker;
    } catch (error) {
      console.error("Error initializing datepickers:", error);
    }
  },

  destroy() {
    if (this.startDatePicker) {
      this.startDatePicker.destroy();
    }
    if (this.endDatePicker) {
      this.endDatePicker.destroy();
    }
  },
};
